import { Anchor, Table, Text } from "@mantine/core";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnAnalyticsApi } from "../../../apis";
import { currencyFormat } from "../../../utils/currency";
import { ListState } from "../../ui";
import { DetailCard } from "../detail-card";
import { TrustBadge } from "../trust-badge";

interface IProps {
  dateFromIso?: string;
  dateToIso?  : string;
  limit?      : number;
}

const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

export function TopBuyersList({ dateFromIso, dateToIso, limit = 10 }: IProps) {
  const { t } = useTranslation("returns");
  const { data, error, mutate } = useSWR(
    ["returns.top-buyers", dateFromIso, dateToIso, limit],
    () => ReturnAnalyticsApi.repeatReturners({ threshold: 1, limit, dateFrom: dateFromIso, dateTo: dateToIso }),
    { revalidateOnFocus: false }
  );

  const rows = data?.buyers ?? [];

  return (
    <DetailCard title={t("dashboard.topBuyers.title")}>
      {error && !data ? (
        <ListState state="error" onRetry={() => mutate()} />
      ) : rows.length === 0 ? (
        <ListState state="empty" title={t("dashboard.topBuyers.empty")} />
      ) : (
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("col.buyer")}</Table.Th>
              <Table.Th>{t("listTable.col.trust")}</Table.Th>
              <Table.Th ta="right">{t("col.returns")}</Table.Th>
              <Table.Th ta="right">{t("col.totalReturnsAmount")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((b) => (
              <Table.Tr key={b.buyerBusinessId}>
                <Table.Td>
                  <Anchor component={Link} to={`/returns/trust-scores/${b.buyerBusinessId}`} fz="var(--nw-fz-body)" c="var(--nw-text)">
                    {b.buyerBusinessName || "-"}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  {b.buyerBusinessId ? <TrustBadge businessId={b.buyerBusinessId} /> : "-"}
                </Table.Td>
                <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{b.returnCount}</Text></Table.Td>
                <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{currencyFormat(b.totalRefundAmount ?? 0)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </DetailCard>
  );
}
